/**
 * תצוגת מחירים במסלול searchoffer / סיכום הזמנה — לפי מטבע החיפוש (USD/ILS),
 * בלי convertPrice / formatIlsDisplay וללא ₪ כברירת מחדל.
 * ערכי ourPrice מ־OrderContext (priceWithCommission, handlingFee, priceWithCreditCardFee)
 * הם בסיס USD אחיד; סכומי טיסה/תוספות מקושרות ב־Summary מסורתית ב־ILS.
 */

import React from "react";
import { formatHotelSearchAmountForDisplay } from "../constants/hotelCurrencies";
import { resolveHotelProfileRoomDisplayCurrency } from "./hotelSellingPrice";

export function resolveSearchOfferDisplayCurrency({ selectedHotelCurrency, searchPhrase }) {
  return resolveHotelProfileRoomDisplayCurrency({
    requestCurrency: selectedHotelCurrency,
    searchPhrase: searchPhrase ?? null,
    hotelInfo: null,
    pkg: null,
  });
}

/**
 * @param {number} amountUsd
 * @param {'USD'|'ILS'|null|undefined} displayCurrency
 * @param {(n: number, fixed?: boolean) => number} usdToIls
 * @param {{ ceilIls?: boolean }} [opts]
 */
export function formatHotelOrderUsdFieldForDisplay(
  amountUsd,
  displayCurrency,
  usdToIls,
  opts = {}
) {
  const n = Number(amountUsd);
  if (!Number.isFinite(n)) return "—";
  if (displayCurrency === "ILS" && typeof usdToIls === "function") {
    const ils = usdToIls(n);
    return formatHotelSearchAmountForDisplay(ils, "ILS") ?? String(ils);
  }
  if (displayCurrency === "USD") {
    return formatHotelSearchAmountForDisplay(n, "USD") ?? String(n);
  }
  return formatHotelSearchAmountForDisplay(n, null) ?? String(n);
}

/**
 * סכום שכבר ב־ILS (סל מעורבב: מלון מומר + טיסות וכו').
 * @param {number} totalIls
 * @param {'USD'|'ILS'|null|undefined} displayCurrency
 * @param {(n: number) => number} [ilsToUsd]
 * @param {{ ceilIls?: boolean }} [opts]
 */
/**
 * דמי טיפול: מספר ב־ILS וב־USD מחושבים מראש (ללא המרה נוספת כאן).
 */
export function formatHandlingFeeCompositeForDisplay({
  totalHandlingFeeInIls,
  totalHandlingFeeInUsd,
  displayCurrency,
  isFixedAmount,
}) {
  const ils = Number(totalHandlingFeeInIls);
  const usd = Number(totalHandlingFeeInUsd);
  if (displayCurrency === "USD" && Number.isFinite(usd)) {
    return formatHotelSearchAmountForDisplay(usd, "USD") ?? String(usd);
  }
  if (displayCurrency === "ILS" && Number.isFinite(ils)) {
    return formatHotelSearchAmountForDisplay(ils, "ILS") ?? String(ils);
  }
  if (Number.isFinite(ils)) {
    return formatHotelSearchAmountForDisplay(ils, null) ?? String(ils);
  }
  return "—";
}

export function formatIlsBasketTotalForDisplay(
  totalIls,
  displayCurrency,
  ilsToUsd,
  opts = {}
) {
  const ils = Number(totalIls);
  if (!Number.isFinite(ils)) return "—";
  if (displayCurrency === "ILS") {
    return formatHotelSearchAmountForDisplay(ils, "ILS") ?? String(ils);
  }
  const ilsForFx = opts.ceilIls ? Math.ceil(ils) : ils;
  if (displayCurrency === "USD" && typeof ilsToUsd === "function") {
    const usd = ilsToUsd(ilsForFx);
    return formatHotelSearchAmountForDisplay(usd, "USD") ?? String(usd);
  }
  return formatHotelSearchAmountForDisplay(ilsForFx, null) ?? String(ilsForFx);
}

/**
 * שורת סכום עם דולר משני מ-authoritative displayCurrencies בלבד (לא ilsToUsd על הסכום).
 * לשימוש כשהסל התואם הוא מחיר מלון יחיד כמו בשורת פרופיל.
 *
 * @param {Record<string, unknown>|null|undefined} displayCurrencies
 * @returns {string|import('react').ReactNode}
 */
export function formatIlsBasketTotalWithAuthoritativePackageUsdSecondary(
  totalIls,
  displayCurrency,
  ilsToUsd,
  displayCurrencies,
  opts = {}
) {
  const ilsPart = formatIlsBasketTotalForDisplay(
    totalIls,
    displayCurrency,
    ilsToUsd,
    opts
  );
  if (
    displayCurrencies &&
    typeof displayCurrencies === "object" &&
    displayCurrencies.hasUsd === true &&
    typeof displayCurrencies.formattedUsd === "string" &&
    String(displayCurrencies.formattedUsd).trim() !== ""
  ) {
    return React.createElement(
      React.Fragment,
      null,
      React.createElement("span", { key: "ils" }, ilsPart),
      " ",
      React.createElement(
        "span",
        {
          key: "usd",
          dir: "ltr",
          className: "hotel-package-price-usd-secondary",
        },
        displayCurrencies.formattedUsd
      )
    );
  }
  return ilsPart;
}
